import { ReactElement } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import { FormikProvider, FormikValues, useFormik } from 'formik';

// Components
import { FacetsAccordion, FiltersSelected } from 'components/ContinuousImprovement/Facets/components';
import Button from 'components/Button/Button';

// Styles
import styles from 'components/ContinuousImprovement/Facets/Facets.module.scss';

// Core + Store
import { Facet, IActivityLearnerSummarySearch, IActivitySearchRequest } from 'core/models';
import { IInitialSearchFacet } from '../types';
import { learnerSearchFacetsSelector, searchLeanerResultsLoadingSelector } from 'store/learner/selectors';
import { LEARNER_SEARCH_ACCORDION_TITLES } from 'core/constants';
import { getLearnerSearch, updateLearnerSearch } from 'store/learner/actions';
import { BreakpointsEnum, ButtonVariant } from 'core/enums';
import { useMediaQuery } from '@material-ui/core';
import { isRailOpenSelector } from 'store/rail/selectors';
import { INITIAL_LEARNER_SEARCH_FACETS } from './constants';

// Types
interface IProps {
  handleClose(): void;
  forcedFilter?: IActivityLearnerSummarySearch;
  initialValues: IInitialSearchFacet;
  searchRequest?: IActivitySearchRequest;
}

export const LearnerSearchFacets = (props: IProps): ReactElement => {
  const { handleClose, forcedFilter, initialValues, searchRequest } = props;
  const dispatch = useDispatch();
  const isLgBreakpoint = useMediaQuery(`(max-width:${BreakpointsEnum.lg})`);

  // Selectors.
  const facets: Facet = useSelector(learnerSearchFacetsSelector);
  const isLoading: boolean = useSelector(searchLeanerResultsLoadingSelector);
  const isRailOpen: boolean = useSelector(isRailOpenSelector);

  const formik = useFormik({
    initialValues,
    // API call.
    onSubmit: async (values: FormikValues): Promise<void> => {
      window.scroll(0, 0);
      await dispatch(updateLearnerSearch(values));
      await dispatch(getLearnerSearch(forcedFilter));
      await handleClose();
    },
  });

  const { handleReset, handleSubmit, setValues, values } = formik;

  const onClearAll = async (e): Promise<void> => {
    window.scroll(0, 0);
    await handleReset(e);
    await setValues(INITIAL_LEARNER_SEARCH_FACETS);
    await dispatch(updateLearnerSearch(INITIAL_LEARNER_SEARCH_FACETS));
    await dispatch(getLearnerSearch(forcedFilter));
  };

  const isDisabled = !facets && isLoading;

  return isLgBreakpoint && !isRailOpen ? null : (
    <FormikProvider value={formik}>
      <form className={styles['summary-refiners']} onSubmit={handleSubmit}>
        {isLgBreakpoint && (
          <Button className={styles.close} aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </Button>
        )}
        <Button
          disabled={isDisabled}
          fullWidth
          variant={ButtonVariant.Primary}
          type="submit"
          className={styles['margin-top']}
        >
          Apply selected filter(s)
        </Button>
        {/* Filter pills */}
        <div className={styles.filters}>
          <div className={styles['filters-header']}>
            Filter By:
            <Button className={styles['link link--hyperlink']} disabled={isDisabled} onClick={onClearAll}>
              Clear All
            </Button>
          </div>

          {/* Facets */}
          <div className={styles['filters-selected']}>
            {values && (
              <div className={styles['filters-selected']}>
                <FiltersSelected defaultFacets={LEARNER_SEARCH_ACCORDION_TITLES} />
              </div>
            )}
          </div>
        </div>
        <div className={styles.refiners}>
          <FacetsAccordion
            defaultFacets={LEARNER_SEARCH_ACCORDION_TITLES}
            facets={facets}
            searchRequest={searchRequest}
          />
        </div>
        <Button disabled={isDisabled} fullWidth variant={ButtonVariant.Primary} type="submit">
          Apply selected filter(s)
        </Button>
      </form>
    </FormikProvider>
  );
};
